import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { useWorkspace } from './WorkspaceContext';
import { storageService } from '../services/storageService';
import { StorageQuota, StorageUsage } from '../types/storage';

interface StorageContextType {
  quota: StorageQuota | null;
  usage: StorageUsage | null;
  loading: boolean;
  error: string | null;
  refreshQuota: () => Promise<void>;
  refreshUsage: () => Promise<void>;
  updatePlan: (plan: string) => Promise<void>;
}

const StorageContext = createContext<StorageContextType | undefined>(undefined);

export const useStorage = () => {
  const context = useContext(StorageContext);
  if (context === undefined) {
    throw new Error('useStorage must be used within a StorageProvider');
  }
  return context;
};

interface StorageProviderProps {
  children: ReactNode;
}

export const StorageProvider: React.FC<StorageProviderProps> = ({ children }) => {
  const { currentUser } = useAuth();
  const { currentWorkspace } = useWorkspace();
  const [quota, setQuota] = useState<StorageQuota | null>(null);
  const [usage, setUsage] = useState<StorageUsage | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refreshQuota = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    setError(null);
    
    try {
      let userQuota = await storageService.getUserQuota(currentUser.uid, currentWorkspace?.id);
      
      // Initialize quota if it doesn't exist
      if (!userQuota) {
        userQuota = await storageService.initializeUserQuota(
          currentUser.uid, 
          currentWorkspace?.id, 
          'free'
        );
      }
      
      setQuota(userQuota);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load storage quota');
    } finally {
      setLoading(false);
    }
  };

  const refreshUsage = async () => {
    if (!currentUser) return;
    
    try {
      const storageUsage = await storageService.getStorageUsage(
        currentUser.uid, 
        currentWorkspace?.id
      );
      setUsage(storageUsage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load storage usage');
    }
  };

  const updatePlan = async (plan: string) => {
    if (!currentUser) return;
    
    try {
      await storageService.updateUserQuota(currentUser.uid, currentWorkspace?.id, plan);
      await refreshQuota();
      await refreshUsage();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update storage plan');
    }
  };

  // Load quota and usage when user or workspace changes
  useEffect(() => {
    if (currentUser) {
      refreshQuota();
      refreshUsage();
    } else {
      setQuota(null);
      setUsage(null);
    }
  }, [currentUser, currentWorkspace]);

  const value: StorageContextType = {
    quota,
    usage,
    loading,
    error,
    refreshQuota,
    refreshUsage,
    updatePlan
  };

  return (
    <StorageContext.Provider value={value}>
      {children}
    </StorageContext.Provider>
  );
};

